import { supabase } from '@/lib/supabase';
import { getDeviceInfo, getSessionId } from './deviceInfo';
import { createRateLimit } from '@/lib/security/validation';
import { getReferrerInfo, getUTMParameters } from './referrerTracking';
import { checkCookieConsent, checkUserAnalyticsObjection } from '@/hooks/useAnalyticsConsent';
import type { ClickEvent } from './types';

// Rate limiting: max 100 events per minute per session
const trackEventRateLimit = createRateLimit(60 * 1000, 100);

/**
 * Check if the current page is a Lovable preview (skip tracking)
 */
export const isLovablePreview = (pagePath: string): boolean => {
  return pagePath.includes('forceHideBadge=true') || 
         pagePath.includes('__lovable_token=') ||
         window.location.search.includes('__lovable_token=');
};

/**
 * Detect automated test runs (Playwright/Selenium/Puppeteer/etc.) so e2e
 * traffic does not pollute production analytics. Checks navigator.webdriver
 * (set by all major automation frameworks) and an explicit `?e2e=1` opt-in.
 */
export const isAutomatedTest = (pagePath?: string): boolean => {
  if (typeof navigator !== 'undefined' && (navigator as any).webdriver) return true;
  if (typeof window !== 'undefined' && window.location?.search?.includes('e2e=1')) return true;
  if (pagePath && pagePath.includes('e2e=1')) return true;
  return false;
};

/**
 * Check if URL contains sensitive information
 */
export const containsSensitiveData = (pagePath: string): boolean => {
  const sensitiveParams = ['password', 'token', 'secret', 'key', 'auth'];
  const lowerPath = pagePath.toLowerCase();
  
  return sensitiveParams.some(param => 
    lowerPath.includes(`${param}=`) || 
    lowerPath.includes(`&${param}=`) ||
    lowerPath.includes(`?${param}=`)
  );
};

/**
 * Check if the current user agent is a bot.
 * Bots are not natural persons under GDPR, so consent is not required.
 */
export const isBot = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
    'yandexbot', 'sogou', 'exabot', 'facebot', 'facebookexternalhit',
    'ia_archiver', 'alexa', 'msnbot', 'applebot', 'petalbot',
    'bytespider', 'gptbot', 'claudebot', 'anthropic', 'semrush',
    'ahrefs', 'screaming frog', 'dotbot', 'rogerbot', 'seznambot',
    'crawler', 'spider', 'bot/', 'bot;', 'headless'
  ];
  
  return botPatterns.some(pattern => userAgent.includes(pattern));
};

/**
 * Check if analytics tracking is allowed based on consent and preferences.
 * GDPR Compliance:
 * - Art. 7.3: Respects withdrawal of consent (cookie settings)
 * - Art. 21: Respects right to object (user preferences)
 */
async function canTrackAnalytics(userId?: string): Promise<boolean> {
  // First check cookie consent
  if (!checkCookieConsent()) {
    if (import.meta.env.DEV) {
      console.log('Analytics blocked: no cookie consent');
    }
    return false;
  }
  
  // For authenticated users, also check database preferences
  if (userId) {
    const objectsToAnalytics = await checkUserAnalyticsObjection(userId);
    if (objectsToAnalytics) {
      if (import.meta.env.DEV) {
        console.log('Analytics blocked: user has objected to analytics processing');
      }
      return false;
    }
  }
  
  return true;
}

export const trackPageView = async (
  pagePath: string, 
  userId?: string
): Promise<void> => {
  try {
    // Filter out Lovable editor preview sessions
    if (isLovablePreview(pagePath)) {
      if (import.meta.env.DEV) {
        console.log('Skipping analytics tracking for Lovable preview session:', pagePath);
      }
      return;
    }

    // Skip automated test runs (Playwright/Selenium/etc.) — they pollute analytics
    if (isAutomatedTest(pagePath)) {
      if (import.meta.env.DEV) {
        console.log('Skipping analytics tracking for automated test run:', pagePath);
      }
      return;
    }

    // SECURITY: Block tracking of URLs with sensitive data
    if (containsSensitiveData(pagePath)) {
      console.error('SECURITY: Blocked tracking of URL containing sensitive data:', pagePath);
      return;
    }

    // GDPR Compliance: Check consent before tracking (bots are exempt as they are not natural persons)
    if (!isBot()) {
      const canTrack = await canTrackAnalytics(userId);
      if (!canTrack) {
        return;
      }
    }

    // Get persistent session ID
    const sessionId = getSessionId();
    
    // Apply rate limiting
    if (!trackEventRateLimit(sessionId)) {
      console.warn('Rate limit exceeded for analytics tracking');
      return;
    }

    // Validate and sanitize page path
    const sanitizedPagePath = sanitizePagePath(pagePath);
    
    const deviceInfo = getDeviceInfo();
    
    // Get privacy-compliant referrer and UTM information
    const referrerInfo = getReferrerInfo();
    const utmParams = getUTMParameters();
    
    // Enhanced device info with referrer and UTM data
    const enhancedDeviceInfo = {
      ...deviceInfo,
      referrer_category: referrerInfo.category,
      referrer_source: referrerInfo.source || null,
      is_internal_navigation: referrerInfo.isInternal,
      utm_source: utmParams.utm_source || null,
      utm_medium: utmParams.utm_medium || null,
      utm_campaign: utmParams.utm_campaign || null,
      utm_content: utmParams.utm_content || null
    };
    
    await supabase.rpc('log_usage_event', {
      page_path: sanitizedPagePath,
      action_type: 'page_view',
      user_id: userId || null,
      session_id: sessionId,
      device_info: enhancedDeviceInfo
    });
    
    if (import.meta.env.DEV) {
      console.log('Page view tracked:', { 
        pagePath: sanitizedPagePath, 
        userId, 
        sessionId,
        referrer: referrerInfo.category,
        source: referrerInfo.source
      });
    }
  } catch (error) {
    // Silently fail for analytics to not impact user experience
    console.error('Analytics tracking error:', error);
  }
};

export const trackUserAction = async (
  action: string, 
  pagePath: string, 
  userId?: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  try {
    // Filter out Lovable editor preview sessions
    if (isLovablePreview(pagePath)) {
      if (import.meta.env.DEV) {
        console.log('Skipping analytics tracking for Lovable preview session:', pagePath);
      }
      return;
    }

    // SECURITY: Block tracking of URLs with sensitive data
    if (containsSensitiveData(pagePath)) {
      console.error('SECURITY: Blocked tracking of URL containing sensitive data:', pagePath);
      return;
    }

    // GDPR Compliance: Check consent before tracking (bots are exempt as they are not natural persons)
    if (!isBot()) {
      const canTrack = await canTrackAnalytics(userId);
      if (!canTrack) {
        return;
      }
    }

    // Get persistent session ID
    const sessionId = getSessionId();
    
    // Apply rate limiting
    if (!trackEventRateLimit(sessionId)) {
      console.warn('Rate limit exceeded for analytics tracking');
      return;
    }

    // Validate and sanitize inputs
    const sanitizedPagePath = sanitizePagePath(pagePath);
    const sanitizedAction = sanitizeAction(action);
    
    const deviceInfo = getDeviceInfo();
    const enhancedDeviceInfo = metadata
      ? { ...deviceInfo, event_metadata: metadata }
      : deviceInfo;
    
    await supabase.rpc('log_usage_event', {
      page_path: sanitizedPagePath,
      action_type: sanitizedAction,
      user_id: userId || null,
      session_id: sessionId,
      device_info: enhancedDeviceInfo
    });
    
    if (import.meta.env.DEV) {
      console.log('User action tracked:', { action: sanitizedAction, pagePath: sanitizedPagePath, userId, sessionId });
    }
  } catch (error) {
    // Silently fail for analytics to not impact user experience
    console.error('Analytics tracking error:', error);
  }
};

// Helper functions for input sanitization
export const sanitizePagePath = (path: string): string => {
  // Remove Lovable tokens and other sensitive parameters before storing
  let cleanPath = path;
  
  // Remove __lovable_token parameter
  cleanPath = cleanPath.replace(/[?&]__lovable_token=[^&]*/g, '');
  
  // Remove any credentials that might have leaked into URLs
  cleanPath = cleanPath.replace(/[?&](password|email|token|secret|key|auth)=[^&]*/gi, '');
  
  // Clean up any remaining query parameter artifacts
  cleanPath = cleanPath.replace(/^\?&/, '?').replace(/&$/, '').replace(/\?$/, '');
  
  // Only allow alphanumeric, forward slashes, hyphens, underscores, and clean query parameters
  return cleanPath.replace(/[^a-zA-Z0-9/_?&=-]/g, '').substring(0, 255);
};

const sanitizeAction = (action: string): string => {
  // Only allow alphanumeric, hyphens, underscores, and colons
  return action.replace(/[^a-zA-Z0-9_:-]/g, '').substring(0, 100);
};

/**
 * Track a click event with full device info (Tier 2 - consented users).
 * This provides the same data quality as page views, ensuring consistent
 * analytics across all event types.
 */
export const trackClick = async (
  clickData: ClickEvent,
  userId?: string,
  sessionId?: string
): Promise<void> => {
  try {
    // Skip Lovable preview
    if (isLovablePreview(clickData.page_path)) {
      if (import.meta.env.DEV) {
        console.log('Skipping click tracking for Lovable preview session');
      }
      return;
    }

    // Skip automated test runs
    if (isAutomatedTest(clickData.page_path)) {
      if (import.meta.env.DEV) {
        console.log('Skipping click tracking for automated test run');
      }
      return;
    }

    // Get full device info (same as page views)
    const deviceInfo = getDeviceInfo();
    const referrerInfo = getReferrerInfo();
    const utmParams = getUTMParameters();
    
    // Enhanced device info with click data and referrer/UTM
    const enhancedDeviceInfo = {
      ...deviceInfo,
      referrer_category: referrerInfo.category,
      referrer_source: referrerInfo.source || null,
      is_internal_navigation: referrerInfo.isInternal,
      utm_source: utmParams.utm_source || null,
      utm_medium: utmParams.utm_medium || null,
      utm_campaign: utmParams.utm_campaign || null,
      utm_content: utmParams.utm_content || null,
      // Click-specific fields
      click_element: clickData.element_type,
      click_text: clickData.element_text,
      click_href: clickData.element_href,
      click_label: clickData.track_label,
      click_category: clickData.track_category,
    };
    
    await supabase.rpc('log_usage_event', {
      page_path: sanitizePagePath(clickData.page_path),
      action_type: 'click',
      user_id: userId || null,
      session_id: sessionId || null,
      device_info: enhancedDeviceInfo
    });
    
    if (import.meta.env.DEV) {
      console.log('Click tracked:', { 
        element: clickData.element_type,
        label: clickData.track_label,
        path: clickData.page_path,
        referrer: referrerInfo.source
      });
    }
  } catch (error) {
    // Silently fail for analytics to not impact user experience
    console.error('Click tracking error:', error);
  }
};
